import type { NavigationItem } from '../navigation-item'
import type { ShellExtension } from '../shell-extension'
import { updateSettingsRoutes } from './settings'
import type { NavigationLink, SettingsInfo } from '../../common/global-props'
import { createSettingsHref } from '../../common/settings'
import { isSettings } from './type-checking'
import { convertToKebabCase } from '../../common/helpers/string'
import { getResolvedIcon } from '../../common/icon-helpers'
import { autoResolve } from '../../common/helpers'
import type { Settings, SettingsSection } from '../settings-definition'
import type { TranslatableFnOptions } from '../../common/translate-helpers/types'
import { getShellLogger } from '../../common/logger'

/**
 * Returns an empty array if there is an error loading the navigation items of the extension
 * @param extension Extension to get the navigation items
 * @returns
 */
export const getNavigationItems = (extension: ShellExtension): readonly NavigationItem[] => {
  let navItems: readonly NavigationItem[] = []
  try {
    navItems = extension.getNavigationItems()
  } catch (e) {
    getShellLogger().error(`${extension.id} failed to get navigation items`, e)
  }
  return navItems
}

/**
 * Creates a function which can return the translated key value or the raw key value for an extension
 * @param key Key of value to localize
 * @param extension Extension to use to get the localized value
 * @returns
 */
export const createGetLocalizedStringFunction = (key: string, extension: ShellExtension) => {
  const translatableFn = (options: TranslatableFnOptions = {}) => {
    try {
      const { raw } = options
      if (raw) {
        return key
      }
      return extension.getString(key)
    } catch (e) {
      getShellLogger().error(`${extension.id} failed to get string with key "${key}"`, e)
    }
    return key
  }
  return translatableFn
}

export const getExtensionsSidebarLinks = (extensions: readonly ShellExtension[]): readonly NavigationLink[] => {
  const navigationLinks = extensions.reduce((navigationLinks, extension) => {
    const addNavigationLinks = getNavigationItems(extension).map(navigationItem => {
      const getName = createGetLocalizedStringFunction(navigationItem.displayNameKey, extension)
      return {
        ...navigationItem,
        dataTest: navigationItem.dataTest ?? `${extension.id}/${navigationItem.id}`,
        get icon() {
          return getResolvedIcon(navigationItem.icon, { active: false })
        },
        get name() {
          return getName
        },
        href: navigationItem.pathname,
        isVisible: autoResolve(true),
      } as NavigationLink
    })
    return [...navigationLinks, ...addNavigationLinks]
  }, [] as NavigationLink[])
  return navigationLinks
}

/**
 * Create the settingsInfo for the extension
 * This will produce a list of settings info that will be used as input by the GotoSettings Experience
 * As the SettingsInfo[] is not a tree structure the extension settings will be flatten to 2 levels
 * SettingsInfo[] is used by goto-specific-settings which only display sections and settings
 */
export function createExtensionsSettingsInfo(extensions: readonly ShellExtension[]): readonly SettingsInfo[] {
  let extensionsSettingsInfo: readonly SettingsInfo[] = []
  extensions.forEach(extension => {
    const settingsDefinitions = extension.getSettings()
    settingsDefinitions.forEach(settingsDefinition => {
      if (isSettings(settingsDefinition)) {
        const settingsInfo = convertSettingsToSettingsInfo(settingsDefinition, extension)
        extensionsSettingsInfo = extensionsSettingsInfo.concat(settingsInfo)
        return
      }
      const settingsInfo = convertSettingsSectionToSettingsInfo(settingsDefinition, extension)
      extensionsSettingsInfo = extensionsSettingsInfo.concat(settingsInfo)
    })
  })
  return extensionsSettingsInfo
}

function convertSettingsToSettingsInfo(settingsDefinition: Settings, extension: ShellExtension): SettingsInfo {
  const settingsInfo: SettingsInfo = {
    accordionName: createGetLocalizedStringFunction(settingsDefinition.displayNameKey, extension),
    settingsIcon: settingsDefinition.icon ?? '',
    isVisible: autoResolve(true),
    settingsLinksInfo: [
      {
        name: createGetLocalizedStringFunction(settingsDefinition.displayNameKey, extension),
        href: createSettingsHref(settingsDefinition.route),
      },
    ],
  }
  return settingsInfo
}

function convertSettingsSectionToSettingsInfo(
  settingsDefinition: SettingsSection,
  extension: ShellExtension,
): SettingsInfo {
  const settingsInfo: SettingsInfo = {
    accordionName: createGetLocalizedStringFunction(settingsDefinition.displayNameKey, extension),
    settingsIcon: settingsDefinition.icon ?? '',
    isVisible: autoResolve(true),
    settingsLinksInfo: updateSettingsRoutes(
      settingsDefinition.children,
      convertToKebabCase(settingsDefinition.displayNameKey),
    ).map(settings => ({
      name: createGetLocalizedStringFunction(settings.displayNameKey, extension),
      href: settings.route,
    })),
  }
  return settingsInfo
}
